import {
  BarChartBig,
  Building2,
  Database,
  DatabaseZap,
  DraftingCompass,
  Flag,
  KeyRound,
  Link2,
  Megaphone,
  Phone,
  Users2,
  Wallet
} from 'lucide-react'
import { Navigate } from 'react-router-dom'

import { AppsLayout } from '../modules/apps/page'
import { AppReviewsList } from '../modules/apps/reviews/AppReviewsList'
import { AppsSandboxRequestsList } from '../modules/apps/sandbox-requests/AppsSandboxRequestsList'

import { Layout } from './Layout'
import NotFound from './NotFound'

import { AnnouncementForm } from '@/modules/announcements/announcement-form'
import AnnouncementDetail from '@/modules/announcements/details'
import AnnouncementList from '@/modules/announcements/page'
import AuthenticationPage from '@/modules/authentication/page'
import AwsAccessPage from '@/modules/aws-access/page'
import { BlueprintManifestsList } from '@/modules/blueprints/blueprint-manifests/BlueprintManifestsList'
import { BlueprintResourceDetail } from '@/modules/blueprints/marketplace/blueprint-resources/details'
import { MarketplaceBlueprintResourcesList } from '@/modules/blueprints/marketplace/blueprint-resources/page'
import { BlueprintDetail } from '@/modules/blueprints/marketplace/blueprints/details'
import { MarketplaceBlueprintsList } from '@/modules/blueprints/marketplace/blueprints/page'
import { BlueprintsLayout } from '@/modules/blueprints/page'
import { SandboxRequestsList } from '@/modules/blueprints/sandbox-requests/SandboxRequestsList'
import ConfigAnalyticsAuthorizePage from '@/modules/config-analytics-access/authorize'
import ConfigAnalyticsAccessPage from '@/modules/config-analytics-access/page'
import { EpilotIdpApplicationDetail } from '@/modules/epilot-idp/details'
import {
  CreateEpilotIdpApplication,
  EpilotIdpApplications
} from '@/modules/epilot-idp/page'
import { EpilotIdpAccess } from '@/modules/epilot-idp/shared'
import InternalBI from '@/modules/internal-bi/page'
import { OrganizationForm } from '@/modules/organization/create-organization'
import OrganizationDetail from '@/modules/organization/details'
import OrganizationHubspotCoverage from '@/modules/organization/hubspot-coverage'
import OrganizationList from '@/modules/organization/page'
import { PhoneSetupDetail } from '@/modules/phone/details'
import { CreatePhoneSetup, PhoneSetups } from '@/modules/phone/page'
import { PhoneAccess } from '@/modules/phone/shared'
import PostHogSyncPage from '@/modules/posthog/page'
import { PricingTierForm } from '@/modules/pricing-tier/create-pricing-tier'
import PricingTierDetail from '@/modules/pricing-tier/details'
import PricingTierList from '@/modules/pricing-tier/page'
import { UserDetail } from '@/modules/user/details'
import UserList from '@/modules/user/page'
import VlinkSchemaOverview from '@/modules/vlink-schema/page'
import { Permissions } from '@/utils/permission-manager'

export type Route = {
  path: string
  element: JSX.Element
  icon?: JSX.Element
  label?: string
  showInMenu?: boolean
  children?: Route[]
  permission?: Permissions
}

export enum RoutePaths {
  BLUEPRINTS = '/blueprints',
  APPS = '/apps',
  EPILOT_IDP = '/epilot-idp',
  PHONE = '/phone',
  BLUEPRINT_MANIFESTS = '/blueprints/blueprint-manifests',
  SANDBOX_REQUESTS = '/blueprints/sandbox-requests',
  APP_SANDBOX_REQUESTS = '/apps/sandbox-requests',
  APP_REVIEWS = '/apps/reviews',
  MARKETPLACE_BLUEPRINTS = '/blueprints/marketplace-blueprints',
  MARKETPLACE_BLUEPRINT_DETAILS = '/blueprints/marketplace-blueprints/:blueprintId',
  MARKETPLACE_BLUEPRINT_CREATE = '/blueprints/marketplace-blueprints/create',
  MARKETPLACE_BLUEPRINT_RESOURCES = '/blueprints/marketplace-blueprint-resources',
  MARKETPLACE_BLUEPRINT_RESOURCE_DETAILS = '/blueprints/marketplace-blueprint-resources/:resourceId',
  MARKETPLACE_BLUEPRINT_RESOURCE_CREATE = '/blueprints/marketplace-blueprint-resources/create',
  INTERNAL_BI = '/internal-bi',
  ORGANIZATIONS = '/organizations',
  ORGANIZATION_HUBSPOT_COVERAGE = '/organizations/hubspot-coverage',
  ORGANIZATION_DETAIL = '/organizations/:orgId',
  ORGANIZATION_CREATE = '/organizations/create',
  PLATFORM_ACTIVITY = '/platform-activity',
  PRICING_TIERS = '/pricing-tiers',
  PRICING_TIER_DETAIL = '/pricing-tiers/:pricingTierId',
  PRICING_TIER_CREATE = '/pricing-tiers/create',
  USERS = '/users',
  USER_DETAIL = '/users/:userId',
  VLINK_SCHEMA = '/vlink-schema',
  POSTHOG = '/posthog',
  AWS_ACCESS = '/aws-access',
  CONFIG_ANALYTICS_ACCESS = '/config-analytics-access',
  CONFIG_ANALYTICS_AUTHORIZE = '/config-analytics-access/authorize',
  ANNOUNCEMENTS = '/announcements',
  ANNOUNCEMENT_CREATE = '/announcements/create',
  ANNOUNCEMENT_DETAIL = '/announcements/:announcementId',
  ANNOUNCEMENT_EDIT = '/announcements/:announcementId/edit',
  LOGIN = '/login',
  HOME = '/'
}

const awsAccessEnabled = (): boolean =>
  Boolean(globalThis.EnvConfig?.env?.AWS_ACCESS_ENABLED)

export const routes: Route[] = [
  {
    path: RoutePaths.HOME,
    element: <Layout />,
    children: [
      {
        path: '/',
        element: <Navigate to={RoutePaths.ORGANIZATIONS} />
      },
      {
        path: RoutePaths.ORGANIZATIONS,
        element: <OrganizationList />,
        icon: <Building2 className="text-primary" />,
        label: 'Organizations',
        showInMenu: true
      },
      {
        // moved into Internal BI (first tab); keep old links working
        path: RoutePaths.PLATFORM_ACTIVITY,
        element: <Navigate replace to={RoutePaths.INTERNAL_BI} />
      },
      {
        path: RoutePaths.ORGANIZATION_HUBSPOT_COVERAGE,
        element: <OrganizationHubspotCoverage />,
        icon: <Link2 className="text-primary" />,
        label: 'HubSpot Coverage',
        showInMenu: false
      },
      {
        path: RoutePaths.ORGANIZATION_CREATE,
        element: <OrganizationForm />
      },
      {
        path: RoutePaths.ORGANIZATION_DETAIL,
        element: <OrganizationDetail />
      },
      {
        path: RoutePaths.USERS,
        element: <UserList />,
        icon: <Users2 className="text-primary" />,
        label: 'Users',
        showInMenu: true
      },
      {
        path: RoutePaths.INTERNAL_BI,
        element: <InternalBI />,
        label: 'Internal BI',
        icon: <BarChartBig className="text-primary" />,
        showInMenu: true
      },
      {
        path: RoutePaths.USER_DETAIL,
        element: <UserDetail />
      },
      {
        path: RoutePaths.PRICING_TIERS,
        element: <PricingTierList />,
        label: 'Pricing Tiers',
        icon: <Wallet className="text-primary" />,
        showInMenu: true
      },
      {
        path: RoutePaths.PRICING_TIER_CREATE,
        element: <PricingTierForm />
      },
      {
        path: RoutePaths.PRICING_TIER_DETAIL,
        element: <PricingTierDetail />
      },
      {
        path: RoutePaths.VLINK_SCHEMA,
        element: <VlinkSchemaOverview />,
        label: 'Vlink Database Schema',
        icon: <Database className="text-primary" />,
        showInMenu: true,
        permission: Permissions.VLINK_ADMIN
      },
      {
        path: RoutePaths.POSTHOG,
        element: <PostHogSyncPage />,
        label: 'PostHog',
        icon: <Flag className="text-primary" />,
        showInMenu: true
      },
      {
        path: RoutePaths.ANNOUNCEMENTS,
        element: <AnnouncementList />,
        label: 'Announcements',
        icon: <Megaphone className="text-primary" />,
        showInMenu: true
      },
      {
        path: RoutePaths.ANNOUNCEMENT_CREATE,
        element: <AnnouncementForm />,
        permission: Permissions.MANAGE_ANNOUNCEMENTS
      },
      {
        path: RoutePaths.ANNOUNCEMENT_DETAIL,
        element: <AnnouncementDetail />
      },
      {
        // draft only; permission is checked inside the form (parameterized path)
        path: RoutePaths.ANNOUNCEMENT_EDIT,
        element: <AnnouncementForm />,
        permission: Permissions.MANAGE_ANNOUNCEMENTS
      },
      ...(awsAccessEnabled()
        ? [
            {
              path: RoutePaths.AWS_ACCESS,
              element: <AwsAccessPage />,
              label: 'AWS Access',
              icon: <KeyRound className="text-primary" />,
              showInMenu: true
            }
          ]
        : []),
      {
        path: RoutePaths.CONFIG_ANALYTICS_ACCESS,
        element: <ConfigAnalyticsAccessPage />,
        label: 'Config Analytics Access',
        icon: <DatabaseZap className="text-primary" />,
        showInMenu: true
      },
      {
        // MCP OAuth consent page; the config analytics API redirects here.
        path: RoutePaths.CONFIG_ANALYTICS_AUTHORIZE,
        element: <ConfigAnalyticsAuthorizePage />
      },
      {
        path: RoutePaths.EPILOT_IDP,
        element: <EpilotIdpAccess />,
        label: 'Epilot IdP',
        icon: <KeyRound className="text-primary" />,
        showInMenu: true,
        permission: Permissions.MANAGE_EPILOT_IDP,
        children: [
          {
            path: RoutePaths.EPILOT_IDP,
            element: <EpilotIdpApplications />
          },
          {
            path: `${RoutePaths.EPILOT_IDP}/create`,
            element: <CreateEpilotIdpApplication />
          },
          {
            path: `${RoutePaths.EPILOT_IDP}/:clientId`,
            element: <EpilotIdpApplicationDetail />
          }
        ]
      },
      {
        path: RoutePaths.PHONE,
        element: <PhoneAccess />,
        label: 'Phone',
        icon: <Phone className="text-primary" />,
        showInMenu: true,
        permission: Permissions.MANAGE_PHONE,
        children: [
          {
            path: RoutePaths.PHONE,
            element: <PhoneSetups />
          },
          {
            path: `${RoutePaths.PHONE}/create`,
            element: <CreatePhoneSetup />
          },
          {
            path: `${RoutePaths.PHONE}/:orgId`,
            element: <PhoneSetupDetail />
          }
        ]
      },
      {
        path: RoutePaths.BLUEPRINTS,
        element: <BlueprintsLayout />,
        label: 'Blueprints',
        icon: <DraftingCompass className="text-primary" />,
        showInMenu: true,
        children: [
          {
            path: RoutePaths.BLUEPRINTS,
            element: <Navigate to={RoutePaths.MARKETPLACE_BLUEPRINTS} />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINTS,
            element: <MarketplaceBlueprintsList />,
            showInMenu: true,
            label: 'Marketplace Blueprints',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_RESOURCES,
            element: <MarketplaceBlueprintResourcesList />,
            showInMenu: true,
            label: 'Marketplace Blueprint Resources',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.BLUEPRINT_MANIFESTS,
            element: <BlueprintManifestsList />,
            showInMenu: true,
            label: 'Installed Blueprint Manifests',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.SANDBOX_REQUESTS,
            element: <SandboxRequestsList />,
            showInMenu: true,
            label: 'Sandbox Requests',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_CREATE,
            element: <BlueprintDetail />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_DETAILS,
            element: <BlueprintDetail />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_RESOURCE_CREATE,
            element: <BlueprintResourceDetail />
          },
          {
            path: RoutePaths.MARKETPLACE_BLUEPRINT_RESOURCE_DETAILS,
            element: <BlueprintResourceDetail />
          }
        ]
      },
      {
        path: RoutePaths.APPS,
        element: <AppsLayout />,
        label: 'Apps',
        icon: <DraftingCompass className="text-primary" />,
        showInMenu: true,
        children: [
          {
            path: RoutePaths.APPS,
            element: <Navigate to={RoutePaths.APP_SANDBOX_REQUESTS} />
          },
          {
            path: RoutePaths.APP_SANDBOX_REQUESTS,
            element: <AppsSandboxRequestsList />,
            showInMenu: false,
            label: 'Sandbox Requests',
            icon: <DraftingCompass className="text-primary" />
          },
          {
            path: RoutePaths.APP_REVIEWS,
            element: <AppReviewsList />,
            showInMenu: false,
            label: 'App Review Requests',
            icon: <DraftingCompass className="text-primary" />
          }
        ]
      },
      {
        path: '*',
        element: <NotFound />
      }
    ]
  },
  {
    path: RoutePaths.LOGIN,
    element: <AuthenticationPage />
  }
]

export const getMenuItems = () => {
  return (
    routes
      .find((x) => x.path === RoutePaths.HOME)
      ?.children?.filter((x) => x.showInMenu) ?? []
  )
}

export const getAllRoutes = () => {
  const allRoutes: Route[] = []

  routes.forEach((route) => {
    if (route.children) {
      route.children.forEach((child) => {
        allRoutes.push(child)
      })
    } else {
      allRoutes.push(route)
    }
  })

  return allRoutes
}
